/**
 * 2048 PK 全端横屏多人竞技主控编排中心 (Horizontal Multi-Team & Spectator Arena Coordinator)
 * 模块职责划分：
 * - ui/domRefs.ts: 全局 DOM 节点缓存与 HTML 转义
 * - state/arenaState.ts: 房间、3v3 席位、多选手遥测与本地棋盘运行态存储
 * - lobby/modePortalController.ts: 阶段 1 竞速/无限双模式入口与公开房间列表轮询
 * - lobby/seatLobbyController.ts: 阶段 2 红蓝 3v3 席位、AI 机器人编排、聊天室与 3s 倒计时
 * - game/battlefieldView.ts: 阶段 3 三栏横屏战场微缩盘挂载、观战主视角无缝切换与 HUD 遥测
 * - game/combatSystem.ts: 阶段 3 定点斩半重炮、抛物线弹道路由与 5s 待援 +2 空投系统
 * - game/localBoardController.ts: 阶段 3 本地选手 4x4 滑动合并结算、里程碑/封盘检测与盘面同步
 * - ui/gameoverController.ts: 阶段 4 终局高光定格、战报弹窗与透视复盘
 */

import "./ui/style.css";
import { getArenaHtml } from "./ui/template";
import { ArenaDomRefs, bindArenaDom } from "./ui/domRefs";
import { ArenaRuntimeState } from "./state/arenaState";
import { SignalingClient } from "./network/signaling";
import { P2PChannel } from "./network/p2pChannel";
import { SoundEngine } from "./game/soundEngine";
import { DmcRankManager } from "./game/dmcRankManager";
import { BotPlayer } from "./game/botPlayer";
import { InputController } from "./game/inputController";
import { LocalTileRenderer, RivalTileRenderer } from "./ui/pkRenderer";
import { BattleEffects } from "./ui/battleEffects";
import { BattlefieldView } from "./game/battlefieldView";
import { CombatSystem } from "./game/combatSystem";
import { LocalBoardController } from "./game/localBoardController";
import { ModePortalController } from "./lobby/modePortalController";
import { SeatLobbyController } from "./lobby/seatLobbyController";
import { GameoverController } from "./ui/gameoverController";
import { getThemeByMaxTile, evaluateDangerState, Tile } from "./game/boardEngine";
import type {
  MoveDirection,
  PKGamePacket,
  GameMode,
  SeatOccupant,
  RoomLobbyState,
  MatchParticipantSnapshot,
} from "../shared/protocol";

export class Game2048PKApp {
  private static readonly DISCONNECT_GRACE_MS = 12000;
  private readonly container: HTMLElement;
  private readonly dom: ArenaDomRefs;
  private readonly state: ArenaRuntimeState;
  private readonly signaling: SignalingClient;
  private readonly p2p: P2PChannel;
  private readonly sounds: SoundEngine;
  private readonly dmc: DmcRankManager;
  private readonly effects: BattleEffects;
  private readonly localRenderer: LocalTileRenderer;
  private readonly spectatorMainRenderer: RivalTileRenderer;
  private readonly primaryEnemyRenderer: RivalTileRenderer;
  private readonly view: BattlefieldView;
  private readonly combat: CombatSystem;
  private readonly boardCtrl: LocalBoardController;
  private readonly portalCtrl: ModePortalController;
  private readonly seatCtrl: SeatLobbyController;
  private readonly gameoverCtrl: GameoverController;
  private inputCtrl: InputController | null = null;
  private disconnectGraceTimers = new Map<string, number>();

  constructor(target: HTMLElement) {
    this.container = target;
    this.container.innerHTML = getArenaHtml();

    this.dom = bindArenaDom(this.container);
    this.signaling = new SignalingClient();
    this.state = new ArenaRuntimeState(this.signaling.peerId);
    this.p2p = new P2PChannel(this.signaling);
    this.sounds = new SoundEngine();
    this.dmc = new DmcRankManager();
    this.dmc.bindSounds(this.sounds);

    this.localRenderer = new LocalTileRenderer(
      this.dom.elLocalTileContainer,
      this.dom.elLocalGridCells
    );
    this.spectatorMainRenderer = new RivalTileRenderer(
      this.dom.elLocalTileContainer,
      this.dom.elLocalGridCells
    );
    this.primaryEnemyRenderer = new RivalTileRenderer(
      this.dom.elRivalTileContainer,
      this.dom.elRivalGridCells
    );
    this.effects = new BattleEffects(this.dom.elEffectOverlay);

    this.view = new BattlefieldView(
      this.dom,
      this.state,
      this.localRenderer,
      this.spectatorMainRenderer,
      this.primaryEnemyRenderer
    );

    this.gameoverCtrl = new GameoverController(
      this.dom,
      this.state,
      this.sounds,
      this.dmc,
      this.effects,
      this.view,
      {
        onReturnToSeats: () => this.backToSeatLobby(),
        onReturnToModePortal: () => this.returnToModePortal(),
        hasActiveOrTransferableEject: (team) =>
          this.combat.hasActiveOrTransferableEject(team),
      }
    );

    this.combat = new CombatSystem(
      this.dom,
      this.state,
      this.p2p,
      this.sounds,
      this.localRenderer,
      this.effects,
      this.view,
      {
        onFlushPendingMove: () => this.boardCtrl.flushPendingMoveAnimation(),
        onBroadcastBoardState: () => this.boardCtrl.broadcastBoardState(),
        onHandleIncomingPacket: (pkt) => this.handleIncomingPacket(pkt),
        onEnterLocalStuckPendingEject: () =>
          this.boardCtrl.enterLocalStuckPendingEject(),
        onConsumeLocalPendingEjectTile: () =>
          this.boardCtrl.consumeLocalPendingEjectTile(),
        onEvaluateTeamStuckConditions: () =>
          this.gameoverCtrl.evaluateTeamStuckConditions(),
      }
    );

    this.boardCtrl = new LocalBoardController(
      this.dom,
      this.state,
      this.p2p,
      this.sounds,
      this.dmc,
      this.localRenderer,
      this.effects,
      this.view,
      this.combat,
      this.gameoverCtrl
    );

    this.portalCtrl = new ModePortalController(this.dom, {
      onEnterNewRoomWithMode: (mode) => this.enterNewRoomWithMode(mode),
      onJoinExistingRoom: (roomId) => this.joinExistingRoom(roomId),
    });

    this.seatCtrl = new SeatLobbyController(
      this.dom,
      this.state,
      this.signaling,
      this.p2p,
      this.sounds,
      this.view,
      {
        onReturnToModePortal: () => this.returnToModePortal(),
        onLaunchMatch: (lobbyState) => this.launchMatchFromLobby(lobbyState),
      }
    );

    this.setupGlobalEvents();
    this.setupSignalingEvents();
    this.setupP2PEvents();

    try {
      const savedName = sessionStorage.getItem("pk2048_player_name");
      if (savedName && this.dom.elInputPlayerName) {
        this.dom.elInputPlayerName.value = savedName;
      }
    } catch (_) {}

    this.portalCtrl.startLobbyPolling();
    this.portalCtrl.checkUrlRoomParams();
  }

  private setupGlobalEvents() {
    window.addEventListener("beforeunload", () => {
      if (this.state.isMatchRunning && !this.state.hasFinished) {
        this.saveMatchSnapshotToSession();
      }
    });

    window.addEventListener("resize", () => {
      if (this.state.myTeam === "spectator") {
        this.spectatorMainRenderer.updateCoordsCache();
      } else {
        this.localRenderer.repositionAll(this.state.localTiles);
      }
      for (const p of this.state.participants.values()) {
        p.renderer?.updateCoordsCache();
      }
    });

    const swipeZone =
      (this.container.querySelector(".pk-battlefield") as HTMLElement) ||
      this.dom.elLocalBoardWrapper;
    this.inputCtrl = new InputController(swipeZone, (dir) => {
      this.boardCtrl.handlePlayerMove(dir);
    });

    this.dmc.onRankChange((grade, gauge) => {
      this.dom.elLocalDmcLetter.textContent = grade;
      this.dom.elLocalDmcLetter.className = `dmc-letter rank-${grade.toLowerCase()}`;
      this.dom.elLocalDmcGaugeBar.style.width = `${gauge}%`;
      const me = this.state.participants.get(this.state.myPeerId);
      if (me && this.state.myTeam !== "spectator") {
        this.state.updateParticipantPeakGrade(me, grade);
      }
      if (this.state.isMatchRunning && this.state.myTeam !== "spectator") {
        this.view.updateLocalStatsUI();
      }
    });

    this.dom.elBtnSoundToggle.addEventListener("click", () => {
      const muted = this.sounds.toggleMute();
      this.dom.elSoundIconOn.style.display = muted ? "none" : "block";
      this.dom.elSoundIconOff.style.display = muted ? "block" : "none";
    });

    this.dom.elBtnDpadToggle.addEventListener("click", () => {
      const hidden = this.dom.elVirtualDpad.style.display === "none";
      this.dom.elVirtualDpad.style.display = hidden ? "grid" : "none";
    });

    this.dom.elVirtualDpad.querySelectorAll(".dpad-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const dir = (e.currentTarget as HTMLElement).getAttribute("data-dir") as MoveDirection;
        if (dir) this.boardCtrl.handlePlayerMove(dir);
      });
    });

    const copyRoomLink = (btnEl: HTMLElement) => {
      if (!this.state.roomId) return;
      const shareUrl = `${window.location.origin}${window.location.pathname}?room=${this.state.roomId}`;
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.view.logCombat(`房间 #${this.state.roomId} 链接已复制`);
        const origHtml = btnEl.innerHTML;
        btnEl.textContent = "已复制";
        setTimeout(() => {
          btnEl.innerHTML = origHtml;
        }, 1200);
      });
    };
    this.dom.elBtnCopyLink.addEventListener("click", () =>
      copyRoomLink(this.dom.elBtnCopyLink)
    );
    this.dom.elBtnSeatCopyLink.addEventListener("click", () =>
      copyRoomLink(this.dom.elBtnSeatCopyLink)
    );

    this.dom.elBtnExitGame.addEventListener("click", () => {
      if (
        this.state.isMatchRunning &&
        !this.state.hasFinished &&
        this.state.myTeam !== "spectator"
      ) {
        if (confirm("对局正在进行中，确定要返回席位大厅吗？")) {
          this.backToSeatLobby();
        }
      } else {
        this.backToSeatLobby();
      }
    });

    this.container.querySelectorAll(".btn-emote").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const emoji = (e.currentTarget as HTMLElement).getAttribute("data-emoji");
        if (emoji) {
          this.sounds.playEmote();
          this.effects.spawnFloatingEmote(emoji, false);
          this.p2p.sendPacket({
            type: "emote",
            emoji,
            sender: this.state.myName,
            senderId: this.state.myPeerId,
          });
        }
      });
    });

    this.dom.elPrimaryEnemyCard.addEventListener("click", () => {
      const targetId = this.dom.elPrimaryEnemyCard.getAttribute("data-player-id");
      if (targetId && this.state.myTeam === "spectator") {
        this.view.switchSpectatorFocus(targetId);
      }
    });
  }

  private setupSignalingEvents() {
    this.signaling.on("connected", () => {
      this.dom.elHudNetDot.className = "net-dot";
      this.dom.elHudNetText.textContent = "在线同步";
    });

    this.signaling.on("disconnected", () => {
      this.dom.elHudNetDot.className = "net-dot disconnected";
      this.dom.elHudNetText.textContent = "重连中...";
    });

    this.signaling.on("room_created", ({ roomId }) => {
      this.portalCtrl.hideLoading();
      this.state.roomId = roomId;
      this.dom.elHudRoomCode.textContent = roomId;
      this.dom.elSeatRoomCode.textContent = roomId;
      this.seatCtrl.renderSeatLobby();
    });

    this.signaling.on("room_joined", ({ roomId }) => {
      this.portalCtrl.hideLoading();
      this.state.roomId = roomId;
      this.dom.elHudRoomCode.textContent = roomId;
      this.dom.elSeatRoomCode.textContent = roomId;
      this.seatCtrl.renderSeatLobby();
    });

    this.signaling.on("peer_left", (_reason, peerId) => {
      if (peerId && this.state.isMatchRunning && !this.state.hasFinished) {
        this.startParticipantDisconnectGrace(peerId);
      }
    });

    this.signaling.on("room_peers_sync", ({ roomId, hostId, peers, eventText }) => {
      this.portalCtrl.hideLoading();
      this.state.roomId = roomId;
      this.state.lobbyState.roomId = roomId;
      this.state.lobbyState.hostId = hostId;
      this.dom.elHudRoomCode.textContent = roomId;
      this.dom.elSeatRoomCode.textContent = roomId;

      this.state.knownPeers.clear();
      for (const p of peers) {
        this.state.knownPeers.set(p.peerId, p.name);
      }

      if (eventText) {
        this.seatCtrl.appendSystemChat(eventText);
      }

      // 若是单人+AI房刷新页面重连，先尝试从 sessionStorage 恢复未完对局
      if (this.state.isRoomHost() && !this.state.isMatchRunning && peers.length === 1) {
        this.tryRestoreMatchFromSession(roomId);
      }

      if (this.state.isMatchRunning && !this.state.hasFinished) {
        if (this.state.myTeam !== "spectator") {
          this.dom.elLocalRole.textContent = this.state.isRoomHost() ? "HOST" : "PLAYER";
        }
        // 若原房主掉线且本机顺延成为新房主，无缝接管场上所有 AI 机器人；若本机已失去房主身份则停止本地旧 AI
        if (this.state.isRoomHost()) {
          this.ensureHostActiveBots();
        } else if (this.state.activeBots.size > 0) {
          for (const bot of this.state.activeBots.values()) {
            bot.stop();
          }
          this.state.activeBots.clear();
        }
        // 检查局内真人选手的在线/重连状态（12 秒宽限期）
        for (const p of this.state.participants.values()) {
          if (p.isBot || p.id === this.state.myPeerId) continue;
          if (this.state.knownPeers.has(p.id)) {
            this.clearParticipantDisconnectGrace(p.id, true);
          } else {
            this.startParticipantDisconnectGrace(p.id);
          }
        }
      }

      if (this.state.isRoomHost()) {
        this.seatCtrl.reconcilePeersIntoSeats(peers);
        if (this.state.isMatchRunning && !this.state.hasFinished) {
          this.state.lobbyState.status = "playing";
          this.broadcastMatchSnapshot();
        } else {
          this.seatCtrl.broadcastLobbyState();
        }
      }

      this.seatCtrl.renderSeatLobby();
    });

    this.signaling.on("room_error", (message) => {
      this.portalCtrl.hideLoading();
      if (this.state.isMatchRunning && !this.state.hasFinished) {
        console.warn("[Signaling Notice]", message);
        return;
      }
      alert(message);
    });
  }

  private setupP2PEvents() {
    this.p2p.onStatus((status) => {
      if (status.mode === "p2p" || status.mode === "cf_relay") {
        this.dom.elHudNetDot.className = "net-dot";
        this.dom.elHudNetText.textContent =
          status.rtt > 0 ? `同步 (${status.rtt}ms)` : "实时同步";
      }
    });

    this.p2p.onPacket((packet, fromPeerId) => {
      this.handleIncomingPacket(packet, fromPeerId);
    });
  }

  private syncRoomUrl(roomId: string | null) {
    try {
      const nextUrl = roomId
        ? `${window.location.pathname}?room=${encodeURIComponent(roomId)}`
        : window.location.pathname;
      window.history.replaceState(null, "", nextUrl);
    } catch (_) {}
  }

  private savePlayerNameToSession(name: string) {
    try {
      sessionStorage.setItem("pk2048_player_name", name);
    } catch (_) {}
  }

  private enterNewRoomWithMode(mode: GameMode) {
    this.state.myName = this.dom.elInputPlayerName.value.trim() || "数字猎手";
    this.savePlayerNameToSession(this.state.myName);
    this.dom.elLocalName.textContent = this.state.myName;
    this.state.gameMode = mode;
    this.portalCtrl.stopLobbyPolling();

    const roomId = Math.floor(100000 + Math.random() * 900000).toString();
    this.state.roomId = roomId;
    this.syncRoomUrl(roomId);
    this.state.knownPeers.clear();
    this.state.knownPeers.set(this.state.myPeerId, this.state.myName);

    this.state.lobbyState = this.state.createInitialLobbyState(roomId, mode);
    this.view.syncModePillUI();

    this.dom.elModalLobby.style.display = "none";
    this.dom.elModalSeatLobby.style.display = "flex";
    this.dom.elSeatChatList.innerHTML = "";
    this.dom.elBattleChatList.innerHTML = "";

    const modeName =
      mode === "endless"
        ? "无限模式"
        : mode === "speed_2048"
          ? "极速模式"
          : "标准模式";
    this.seatCtrl.appendSystemChat(`房间 #${roomId} (${modeName}) 已创建`);
    this.seatCtrl.renderSeatLobby();

    this.signaling.connectToRoom(roomId, "host", this.state.myName, mode).catch(() => {});
  }

  private joinExistingRoom(roomId: string) {
    this.state.myName = this.dom.elInputPlayerName.value.trim() || "挑战者";
    this.savePlayerNameToSession(this.state.myName);
    this.dom.elLocalName.textContent = this.state.myName;
    this.portalCtrl.stopLobbyPolling();

    this.state.roomId = roomId;
    this.syncRoomUrl(roomId);
    this.state.lobbyState = {
      roomId,
      hostId: "",
      gameMode: this.state.gameMode,
      redSeats: [null, null, null],
      blueSeats: [null, null, null],
      spectators: [],
      status: "lobby",
      countdownSec: 3,
    };

    this.dom.elModalLobby.style.display = "none";
    this.dom.elModalSeatLobby.style.display = "flex";
    this.dom.elSeatChatList.innerHTML = "";
    this.dom.elBattleChatList.innerHTML = "";
    this.seatCtrl.appendSystemChat(`正在连接房间 #${roomId} 席位大厅...`);
    this.seatCtrl.renderSeatLobby();

    this.signaling
      .connectToRoom(roomId, "guest", this.state.myName, this.state.gameMode)
      .catch(() => {
        this.portalCtrl.hideLoading();
      });
  }

  /**
   * 阶段 2 -> 阶段 3：根据当前席位配置启动对称/非对称/观战实战对局（支持全量快照恢复）
   */
  private launchMatchFromLobby(
    state: RoomLobbyState,
    snapshotParticipants?: MatchParticipantSnapshot[],
    remainingSec?: number
  ) {
    this.clearAllDisconnectGraceTimers();
    this.boardCtrl.flushPendingMoveAnimation();
    this.combat.resetEjectTracking();
    this.seatCtrl.clearCountdownTimer();
    this.gameoverCtrl.clearTimers();

    this.state.lobbyState = state;
    this.state.gameMode = state.gameMode;
    this.state.myTeam = this.state.getMyCurrentSeatTeam();
    this.view.syncModePillUI();

    this.dom.elModalLobby.style.display = "none";
    this.dom.elModalSeatLobby.style.display = "none";
    this.dom.elModalGameover.classList.remove("inspecting-board");
    this.dom.elModalGameover.style.display = "none";
    this.dom.elLocalBoardWrapper.classList.remove(
      "board-climax-win",
      "board-climax-lose",
      "board-overcharge-aura"
    );

    this.state.resetMatchStats();

    const snapMap = new Map<string, MatchParticipantSnapshot>();
    if (snapshotParticipants) {
      for (const s of snapshotParticipants) {
        snapMap.set(s.id, s);
      }
    }

    const allOccupants: SeatOccupant[] = [
      ...(state.redSeats.filter(Boolean) as SeatOccupant[]),
      ...(state.blueSeats.filter(Boolean) as SeatOccupant[]),
    ];

    for (const occ of allOccupants) {
      const snap = snapMap.get(occ.id);
      this.state.participants.set(occ.id, {
        id: occ.id,
        name: occ.name,
        team: occ.team,
        isBot: occ.isBot,
        score: snap ? snap.score : 0,
        maxTile: snap ? snap.maxTile : 2,
        styleGrade: snap ? snap.styleGrade : "D",
        peakGrade: snap ? snap.peakGrade : "D",
        slashHits: snap ? snap.slashHits : 0,
        scoreDrained: snap ? snap.scoreDrained : 0,
        shieldBlocks: snap ? snap.shieldBlocks : 0,
        ejectCount: snap ? snap.ejectCount : 0,
        tiles: snap ? snap.tiles.map((t) => ({ ...t })) : [],
        inDanger: snap ? Boolean(snap.inDanger) : false,
        isStuck: snap ? Boolean(snap.isStuck) : false,
        isExited: snap ? Boolean(snap.isExited) : false,
        lastMoveAt: Date.now(),
      });
    }

    if (this.state.myTeam === "spectator") {
      const firstPlayer = allOccupants[0];
      this.state.spectatorFocusId = firstPlayer ? firstPlayer.id : null;
    } else {
      this.state.spectatorFocusId = null;
    }

    this.localRenderer.clearAll();
    this.spectatorMainRenderer.clearAll();
    this.localRenderer.updateCoordsCache();
    this.spectatorMainRenderer.updateCoordsCache();
    this.dmc.reset();

    this.view.setupBattlefieldBoards();

    // 同步快照中所有远端/队友微缩棋盘的方块与状态标签
    for (const p of this.state.participants.values()) {
      if (p.renderer && p.id !== this.state.myPeerId && p.tiles.length > 0) {
        p.renderer.syncRivalTiles(p.tiles);
      }
      this.refreshParticipantCardStatusUI(p);
    }

    if (this.state.myTeam === "spectator") {
      const focusPlayer =
        this.state.getMainBoardParticipant() ||
        (allOccupants[0] ? this.state.participants.get(allOccupants[0].id) : undefined);
      const focusTeam = focusPlayer?.team || "red";
      this.dom.elLocalTeamTag.textContent = focusTeam === "red" ? "RED" : "BLUE";
      this.dom.elLocalTeamTag.style.background = focusTeam === "red" ? "#e11d48" : "#0284c7";
      this.dom.elLocalName.textContent = focusPlayer
        ? `观战: ${focusPlayer.name}`
        : "观战视角";
      this.dom.elLocalRole.textContent = "OB 观战";
      this.dom.elLocalRescueStatus.textContent = "点击棋盘切换视角";
      if (focusPlayer && focusPlayer.tiles.length > 0) {
        this.dom.elLocalScore.textContent = String(focusPlayer.score);
        this.view.syncSpectatorMainBoard(focusPlayer.tiles);
      }
    } else {
      this.dom.elLocalTeamTag.textContent = this.state.myTeam === "red" ? "RED" : "BLUE";
      this.dom.elLocalTeamTag.style.background =
        this.state.myTeam === "red" ? "#e11d48" : "#0284c7";
      this.dom.elLocalName.textContent = this.state.myName;
      this.dom.elLocalRole.textContent = this.state.isRoomHost() ? "HOST" : "PLAYER";

      const mySnap = snapMap.get(this.state.myPeerId);
      if (mySnap && mySnap.tiles.length > 0) {
        this.state.localScore = mySnap.score;
        this.state.localMaxTile = mySnap.maxTile;
        this.state.slashHits = mySnap.slashHits;
        this.state.scoreDrained = mySnap.scoreDrained;
        this.state.shieldBlocks = mySnap.shieldBlocks;
        this.state.isBoardLocked = Boolean(mySnap.isStuck || mySnap.isExited);
        this.state.localTiles = mySnap.tiles.map((t) => ({ ...t } as Tile));
        this.state.nextTileId =
          this.state.localTiles.reduce((max, t) => Math.max(max, t.id || 0), 0) + 1;

        for (const tile of this.state.localTiles) {
          this.localRenderer.mountTile(tile);
          if (tile.isPendingEject) {
            this.localRenderer.setTilePendingEject(tile, true);
          }
        }

        this.dom.elLocalBoardWrapper.setAttribute(
          "data-theme",
          getThemeByMaxTile(this.state.localMaxTile)
        );
        const danger = evaluateDangerState(this.state.localTiles);
        this.dom.elLocalBoardWrapper.classList.toggle("in-danger", danger.isCritical);

        const hasPending = this.state.localTiles.some((t) => Boolean(t.isPendingEject));
        this.dom.elLocalRescueStatus.textContent = mySnap.isExited
          ? "已退出锁定"
          : this.state.isBoardLocked
            ? hasPending
              ? "封盘 · 待发射"
              : "已封盘定格"
            : "攻击蓄能";

        if (!this.state.isBoardLocked) {
          this.dmc.start();
        }
        this.view.updateLocalStatsUI();
        this.boardCtrl.broadcastBoardState();
      } else {
        this.dom.elLocalRescueStatus.textContent = "攻击蓄能";
        this.dmc.start();
        this.boardCtrl.addRandomTile();
        this.boardCtrl.addRandomTile();
        this.view.updateLocalStatsUI();
        this.boardCtrl.broadcastBoardState();
      }
    }

    this.ensureHostActiveBots();

    const redCnt = state.redSeats.filter(Boolean).length;
    const blueCnt = state.blueSeats.filter(Boolean).length;
    this.gameoverCtrl.startMatchTimer();
    if (typeof remainingSec === "number") {
      this.gameoverCtrl.setMatchRemainingSec(remainingSec);
    }
    this.view.updateTeamScoresHUD();
    this.gameoverCtrl.evaluateTeamStuckConditions();
    this.view.logCombat(
      snapshotParticipants ? `重连成功 · 已恢复 ${redCnt}v${blueCnt} 实时战局` : `${redCnt}v${blueCnt} 对决开始`
    );
  }

  private refreshParticipantCardStatusUI(p: {
    score: number;
    isStuck: boolean;
    isExited?: boolean;
    tiles: Array<{ isPendingEject?: boolean }>;
    scoreEl?: HTMLElement;
    statusEl?: HTMLElement;
    cardEl?: HTMLElement;
  }) {
    const hasPendingEject = p.tiles.some((t) => Boolean(t.isPendingEject));
    const statusText = p.isExited
      ? `${p.score} · 已退出`
      : p.isStuck
        ? hasPendingEject
          ? `${p.score} · 待发射`
          : `${p.score} · 封盘`
        : `${p.score}分`;
    if (p.scoreEl) p.scoreEl.textContent = statusText;
    if (p.statusEl) p.statusEl.textContent = statusText;
    if (p.cardEl) {
      p.cardEl.classList.remove("needs-rescue");
      p.cardEl.classList.toggle("is-stuck-locked", Boolean(p.isStuck || p.isExited));
    }
  }

  /**
   * 构造当前对局的全量快照包（由房主在成员重连或盘面变化时生成）
   */
  private buildMatchSnapshotPacket(): Extract<PKGamePacket, { type: "match_snapshot_sync" }> {
    if (this.state.myTeam !== "spectator") {
      const me = this.state.participants.get(this.state.myPeerId);
      if (me) {
        me.score = this.state.localScore;
        me.maxTile = this.state.localMaxTile;
        me.slashHits = Math.max(me.slashHits, this.state.slashHits);
        me.scoreDrained = Math.max(me.scoreDrained, this.state.scoreDrained);
        me.shieldBlocks = Math.max(me.shieldBlocks, this.state.shieldBlocks);
        me.isStuck = this.state.isBoardLocked;
        me.tiles = this.state.localTiles.map((t) => ({
          id: t.id,
          r: t.r,
          c: t.c,
          val: t.val,
          isLastNative: t.isLastNative,
          isTransferred: t.isTransferred,
          isPendingEject: t.isPendingEject,
          isObstacle: t.isObstacle,
        }));
      }
    }

    if (this.state.isRoomHost()) {
      for (const [botId, bot] of this.state.activeBots.entries()) {
        const botRuntime = this.state.participants.get(botId);
        if (botRuntime) {
          botRuntime.score = bot.getScore();
          botRuntime.maxTile = bot.getMaxTile();
          botRuntime.isStuck = bot.isStuck();
          botRuntime.tiles = bot.getTiles().map((t) => ({
            id: t.id,
            r: t.r,
            c: t.c,
            val: t.val,
            isLastNative: t.isLastNative,
            isTransferred: t.isTransferred,
            isPendingEject: t.isPendingEject,
            isObstacle: t.isObstacle,
          }));
        }
      }
    }

    const participants: MatchParticipantSnapshot[] = Array.from(
      this.state.participants.values()
    ).map((p) => ({
      id: p.id,
      name: p.name,
      team: p.team,
      isBot: p.isBot,
      score: p.score,
      maxTile: p.maxTile,
      styleGrade: p.styleGrade,
      peakGrade: p.peakGrade,
      slashHits: p.slashHits,
      scoreDrained: p.scoreDrained,
      shieldBlocks: p.shieldBlocks,
      ejectCount: p.ejectCount,
      tiles: p.tiles.map((t) => ({ ...t })),
      inDanger: p.inDanger,
      isStuck: p.isStuck,
      isExited: p.isExited,
    }));

    return {
      type: "match_snapshot_sync",
      lobbyState: this.state.lobbyState,
      remainingSec: this.gameoverCtrl.getMatchRemainingSec(),
      participants,
    };
  }

  private broadcastMatchSnapshot() {
    if (!this.state.isRoomHost() || !this.state.isMatchRunning || this.state.hasFinished) {
      return;
    }
    const packet = this.buildMatchSnapshotPacket();
    this.saveMatchSnapshotToSession(packet);
    this.p2p.sendPacket(packet);
  }

  private saveMatchSnapshotToSession(
    customPacket?: Extract<PKGamePacket, { type: "match_snapshot_sync" }>
  ) {
    if (!this.state.roomId || !this.state.isMatchRunning || this.state.hasFinished) return;
    try {
      const packet = customPacket || this.buildMatchSnapshotPacket();
      sessionStorage.setItem(
        `pk2048_match_snapshot_${this.state.roomId}`,
        JSON.stringify({ savedAt: Date.now(), packet })
      );
    } catch (_) {}
  }

  private clearMatchSnapshotFromSession(roomId?: string | null) {
    const targetRoom = roomId || this.state.roomId;
    if (!targetRoom) return;
    try {
      sessionStorage.removeItem(`pk2048_match_snapshot_${targetRoom}`);
    } catch (_) {}
  }

  private tryRestoreMatchFromSession(roomId: string): boolean {
    try {
      const raw = sessionStorage.getItem(`pk2048_match_snapshot_${roomId}`);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as {
        savedAt: number;
        packet: Extract<PKGamePacket, { type: "match_snapshot_sync" }>;
      };
      if (!parsed?.packet || Date.now() - parsed.savedAt > 60000) {
        this.clearMatchSnapshotFromSession(roomId);
        return false;
      }
      const elapsedSec = Math.max(0, Math.round((Date.now() - parsed.savedAt) / 1000));
      const nextRemaining = Math.max(1, parsed.packet.remainingSec - elapsedSec);
      this.launchMatchFromLobby(
        parsed.packet.lobbyState,
        parsed.packet.participants,
        nextRemaining
      );
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * 选手网络断开时启动 12 秒重连宽限期（期间保留席位与棋盘，超时未归才判为早退）
   */
  private startParticipantDisconnectGrace(playerId: string) {
    if (!this.state.isMatchRunning || this.state.hasFinished) return;
    const p = this.state.participants.get(playerId);
    if (!p || p.isBot || p.isExited || this.disconnectGraceTimers.has(playerId)) return;

    if (p.scoreEl) p.scoreEl.textContent = `${p.score} · 重连中...`;
    if (p.statusEl) p.statusEl.textContent = `${p.score} · 重连中...`;
    this.view.logCombat(`${p.name} 网络断开，等待重连 (12s)...`);

    const timer = window.setTimeout(() => {
      this.disconnectGraceTimers.delete(playerId);
      if (!this.state.knownPeers.has(playerId)) {
        this.handleParticipantEarlyExit(playerId, `${p.name} 重连超时，已判退出`);
        if (this.state.isRoomHost()) {
          const peersList = Array.from(this.state.knownPeers.entries()).map(
            ([peerId, name]) => ({ peerId, name })
          );
          this.seatCtrl.reconcilePeersIntoSeats(peersList);
          this.seatCtrl.broadcastLobbyState();
        }
      }
    }, Game2048PKApp.DISCONNECT_GRACE_MS);

    this.disconnectGraceTimers.set(playerId, timer);
  }

  private clearParticipantDisconnectGrace(playerId: string, notifyReconnect = false) {
    const timer = this.disconnectGraceTimers.get(playerId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.disconnectGraceTimers.delete(playerId);
      const p = this.state.participants.get(playerId);
      if (p && !p.isExited) {
        this.refreshParticipantCardStatusUI(p);
        if (notifyReconnect) {
          this.view.logCombat(`${p.name} 已重连恢复对战`);
        }
      }
    }
  }

  private clearAllDisconnectGraceTimers() {
    for (const timer of this.disconnectGraceTimers.values()) {
      clearTimeout(timer);
    }
    this.disconnectGraceTimers.clear();
  }

  /**
   * 房主专属：确保场上所有 AI 机器人均由当前房主实例驱动（支持开局初始化与原房主断线后新房主无缝接管）
   */
  private ensureHostActiveBots() {
    if (!this.state.isRoomHost() || !this.state.isMatchRunning || this.state.hasFinished) {
      return;
    }

    for (const p of this.state.participants.values()) {
      if (!p.isBot || this.state.activeBots.has(p.id)) continue;

      const occ: SeatOccupant = {
        id: p.id,
        name: p.name,
        isBot: true,
        team: p.team,
        seatIndex: 0,
      };

      const bot = new BotPlayer(
        (botPacket) => {
          this.combat.handleBotGeneratedPacket(occ, botPacket);
        },
        this.state.gameMode,
        p.id,
        p.name,
        p.team,
        () => {
          this.combat.scheduleOrFlushPendingEjects(p.team);
          this.gameoverCtrl.evaluateTeamStuckConditions();
        }
      );

      this.state.activeBots.set(p.id, bot);
      if (p.tiles.length > 0 || p.score > 0) {
        bot.resumeFromSnapshot({
          tiles: p.tiles,
          score: p.score,
          maxTile: p.maxTile,
          isStuck: p.isStuck,
        });
      } else {
        bot.start();
      }
    }
  }

  /**
   * 处理选手在实战中途提前退出（主动返回大厅或断线离开）：
   * - 将该选手标记为已退出并锁定盘面（清除未发射的待转嫁状态，避免阻塞团队封盘结算）
   * - 立即重算团队封盘/全员退出胜负条件
   */
  private handleParticipantEarlyExit(playerId: string, reasonText?: string) {
    this.clearParticipantDisconnectGrace(playerId, false);
    if (!this.state.isMatchRunning || this.state.hasFinished) return;
    const p = this.state.participants.get(playerId);
    if (!p || p.isExited) return;

    p.isExited = true;
    p.isStuck = true;
    for (const t of p.tiles) {
      t.isPendingEject = false;
    }

    if (p.scoreEl) p.scoreEl.textContent = `${p.score} · 已退出`;
    if (p.statusEl) p.statusEl.textContent = `${p.score} · 已退出`;
    if (p.cardEl) {
      p.cardEl.classList.remove("needs-rescue");
      p.cardEl.classList.add("is-stuck-locked");
    }

    if (this.state.myTeam === "spectator" && this.state.spectatorFocusId === p.id) {
      this.dom.elLocalRescueStatus.textContent = "该选手已退出";
    }

    this.view.logCombat(reasonText || `${p.name} 提前退出了对局`);
    this.seatCtrl.appendSystemChat(reasonText || `${p.name} 提前退出了对局`);
    this.gameoverCtrl.evaluateTeamStuckConditions();
  }

  /**
   * 客户端业务层消息权限校验：
   * 基于服务端 WebSocket 信封认证的真实发送者 ID (senderPeerId) 与权威房主 ID (hostId) 拦截越权消息
   */
  private isAuthorizedPacket(packet: PKGamePacket, senderPeerId: string): boolean {
    const hostId = this.state.lobbyState.hostId;
    const isFromHost = !hostId ? true : senderPeerId === hostId;
    const isBotParticipant = (id?: string) =>
      Boolean(id && (id.startsWith("bot_") || this.state.participants.get(id)?.isBot));
    const isActingForPlayer = (playerId?: string) =>
      Boolean(
        playerId &&
          (senderPeerId === playerId || (isFromHost && isBotParticipant(playerId)))
      );

    switch (packet.type) {
      case "lobby_state_sync":
        return isFromHost && (!packet.state.hostId || packet.state.hostId === senderPeerId);
      case "match_launch":
      case "match_snapshot_sync":
      case "countdown_start":
        return isFromHost;
      case "seat_action_request":
        return senderPeerId === packet.actorId;
      case "countdown_cancel":
        return senderPeerId === packet.cancelerId;
      case "chat_message":
        return senderPeerId === packet.msg.senderId && packet.msg.team !== "system";
      case "emote":
        return !packet.senderId || senderPeerId === packet.senderId;
      case "sync_board":
      case "game_over":
        return isActingForPlayer(packet.playerId);
      case "attack":
        return isActingForPlayer(packet.attack.senderId);
      case "stuck_tile_eject":
        return isActingForPlayer(packet.fromId);
      default:
        return true;
    }
  }

  private handleIncomingPacket(packet: PKGamePacket, fromPeerId?: string) {
    const senderPeerId = fromPeerId || this.state.myPeerId;
    if (!this.isAuthorizedPacket(packet, senderPeerId)) {
      console.warn(
        `[Security] Ignored unauthorized packet "${packet.type}" from peer "${senderPeerId}"`
      );
      return;
    }

    switch (packet.type) {
      case "lobby_state_sync": {
        const prevStatus = this.state.lobbyState.status;
        this.state.lobbyState = packet.state;
        this.state.gameMode = packet.state.gameMode;
        this.seatCtrl.renderSeatLobby();
        if (
          packet.state.status === "playing" &&
          prevStatus !== "playing" &&
          !this.state.isMatchRunning
        ) {
          this.launchMatchFromLobby(packet.state);
        }
        break;
      }

      case "seat_action_request": {
        if (this.state.isRoomHost()) {
          this.seatCtrl.applySeatAction(packet.actorId, packet.actorName, packet.action);
          this.seatCtrl.broadcastLobbyState();
          this.seatCtrl.renderSeatLobby();
        }
        break;
      }

      case "chat_message": {
        this.seatCtrl.appendChatMessage(packet.msg);
        break;
      }

      case "countdown_start": {
        this.seatCtrl.triggerStartCountdown(packet.initiatorName);
        break;
      }

      case "countdown_cancel": {
        this.seatCtrl.cancelStartCountdown(packet.cancelerName);
        break;
      }

      case "match_launch": {
        this.launchMatchFromLobby(packet.state);
        break;
      }

      case "match_snapshot_sync": {
        this.state.lobbyState = packet.lobbyState;
        this.state.gameMode = packet.lobbyState.gameMode;
        this.seatCtrl.renderSeatLobby();

        if (!this.state.isMatchRunning) {
          this.launchMatchFromLobby(
            packet.lobbyState,
            packet.participants,
            packet.remainingSec
          );
        } else if (!this.state.hasFinished) {
          this.gameoverCtrl.setMatchRemainingSec(packet.remainingSec);
          for (const snap of packet.participants) {
            if (snap.id === this.state.myPeerId && this.state.myTeam !== "spectator") {
              continue;
            }
            const p = this.state.participants.get(snap.id);
            if (p) {
              p.score = snap.score;
              p.maxTile = snap.maxTile;
              p.styleGrade = snap.styleGrade;
              p.peakGrade = snap.peakGrade;
              p.slashHits = snap.slashHits;
              p.scoreDrained = snap.scoreDrained;
              p.shieldBlocks = snap.shieldBlocks;
              p.ejectCount = snap.ejectCount;
              p.tiles = snap.tiles.map((t) => ({ ...t }));
              p.inDanger = snap.inDanger;
              p.isStuck = Boolean(snap.isStuck);
              p.isExited = Boolean(snap.isExited);
              if (p.renderer && p.id !== this.state.myPeerId) {
                p.renderer.syncRivalTiles(p.tiles);
              }
              this.refreshParticipantCardStatusUI(p);
            }
          }
          if (this.state.myTeam !== "spectator") {
            this.boardCtrl.broadcastBoardState();
          }
          this.view.updateTeamScoresHUD();
          this.gameoverCtrl.evaluateTeamStuckConditions();
        }
        break;
      }

      case "sync_board": {
        const pid = packet.playerId;
        const p = pid ? this.state.participants.get(pid) : undefined;
        if (p && !p.isExited) {
          p.score = packet.score;
          p.maxTile = packet.maxTile;
          this.state.updateParticipantPeakGrade(p, packet.styleGrade || "D");
          p.tiles = packet.tiles.map((t) => ({ ...t }));
          p.inDanger = packet.inDanger;
          p.isStuck = Boolean(packet.isStuck);
          p.lastMoveAt = Date.now();

          if (p.renderer && p.id !== this.state.myPeerId) {
            p.renderer.syncRivalTiles(packet.tiles);
          }
          const hasPendingEject = packet.tiles.some((t) => Boolean(t.isPendingEject));
          if (p.scoreEl) {
            p.scoreEl.textContent = p.isStuck
              ? hasPendingEject
                ? `${packet.score} · 待发射`
                : `${packet.score} · 封盘`
              : `${packet.score}分`;
          }
          if (p.statusEl) {
            p.statusEl.textContent = p.isStuck
              ? hasPendingEject
                ? `${packet.score} · 待发射`
                : `${packet.score} · 封盘`
              : `${packet.score}分`;
          }
          if (p.cardEl) {
            p.cardEl.classList.remove("needs-rescue");
            p.cardEl.classList.toggle("is-stuck-locked", p.isStuck);
          }

          if (p.id === this.state.lockedEnemyId) {
            this.dom.elRivalDmcLetter.textContent = packet.styleGrade;
            this.dom.elRivalDmcLetter.className = `dmc-letter rank-${packet.styleGrade.toLowerCase()}`;
            this.dom.elRivalDmcGaugeBar.style.width = `${packet.styleGauge}%`;
            this.dom.elRivalMirrorWrapper.classList.toggle("in-danger", packet.inDanger);
          }

          if (this.state.myTeam === "spectator" && this.state.spectatorFocusId === p.id) {
            this.dom.elLocalScore.textContent = String(packet.score);
            this.dom.elLocalDmcLetter.textContent = packet.styleGrade;
            this.dom.elLocalDmcLetter.className = `dmc-letter rank-${packet.styleGrade.toLowerCase()}`;
            this.dom.elLocalDmcGaugeBar.style.width = `${packet.styleGauge}%`;
            this.dom.elLocalChargeFill.style.width = `${Math.min(100, packet.attackGauge ?? 0)}%`;
            const enemySide: "red" | "blue" = p.team === "red" ? "blue" : "red";
            const isCrit = this.state.isUnderdogCritAgainst(
              p.score,
              this.state.getTeamMaxScore(enemySide)
            );
            this.dom.elLocalChargeStatus.textContent = isCrit ? "暴击态" : "压制态";
            this.dom.elLocalBoardWrapper.setAttribute(
              "data-theme",
              getThemeByMaxTile(packet.maxTile)
            );
            this.dom.elLocalBoardWrapper.classList.toggle("in-danger", packet.inDanger);
            this.view.syncSpectatorMainBoard(packet.tiles);
          }

          // 若该选手盘面出现空位或处于封盘待发射状态，立即检查本阵营是否有待发射方块可发射
          this.combat.scheduleOrFlushPendingEjects(p.team);
        }

        this.view.updateTeamScoresHUD();
        this.gameoverCtrl.evaluateTeamStuckConditions();
        break;
      }

      case "attack": {
        if (packet.attack.senderId !== this.state.myPeerId) {
          this.sounds.playLaunchOrb();
          this.combat.animateAttackBetweenParticipants(packet.attack);
        }
        break;
      }

      case "stuck_tile_eject": {
        this.combat.animateStuckTileEject(packet);
        break;
      }

      case "emote": {
        this.sounds.playEmote();
        this.effects.spawnFloatingEmote(packet.emoji, true);
        break;
      }

      case "game_over": {
        if (!this.state.hasFinished) {
          if (packet.cause === "REACHED_2048") {
            const winTeam =
              packet.winningTeam ||
              (packet.playerId
                ? this.state.participants.get(packet.playerId)?.team
                : undefined) ||
              "blue";
            const viewTeam = this.state.getViewTeam();
            const isMyWin = viewTeam === winTeam;
            const heroName = packet.playerName || "选手";
            this.gameoverCtrl.triggerGameOver(
              isMyWin,
              `合出 2048 绝杀！${winTeam === "red" ? "红方" : "蓝方"}【${heroName}】夺冠`
            );
          } else if (packet.cause === "STUCK") {
            if (packet.playerId) {
              const p = this.state.participants.get(packet.playerId);
              if (p && !p.isExited) {
                p.isStuck = true;
                const hasPending = p.tiles.some((t) => Boolean(t.isPendingEject));
                const tag = hasPending ? "待发射" : "封盘";
                if (p.scoreEl) p.scoreEl.textContent = `${p.score} · ${tag}`;
                if (p.statusEl) p.statusEl.textContent = `${p.score} · ${tag}`;
                if (p.cardEl) {
                  p.cardEl.classList.remove("needs-rescue");
                  p.cardEl.classList.add("is-stuck-locked");
                }
                this.combat.scheduleOrFlushPendingEjects(p.team);
              }
            }
            this.gameoverCtrl.evaluateTeamStuckConditions();
          } else if (packet.cause === "SURRENDER") {
            if (packet.playerId) {
              this.handleParticipantEarlyExit(
                packet.playerId,
                packet.reasonText || `${packet.playerName || "选手"} 提前退出了对局`
              );
            }
          }
        }
        break;
      }
    }
  }

  private notifyEarlyExitIfInMatch() {
    if (
      this.state.isMatchRunning &&
      !this.state.hasFinished &&
      this.state.myTeam !== "spectator"
    ) {
      this.p2p.sendPacket({
        type: "game_over",
        playerId: this.state.myPeerId,
        playerName: this.state.myName,
        finalScore: this.state.localScore,
        maxTile: this.state.localMaxTile,
        duration: 0,
        reasonText: `${this.state.myName} 提前退出了对局`,
        cause: "SURRENDER",
      });
    }
  }

  private backToSeatLobby() {
    this.notifyEarlyExitIfInMatch();
    this.clearMatchSnapshotFromSession();
    this.clearAllDisconnectGraceTimers();
    this.boardCtrl.flushPendingMoveAnimation();
    this.combat.resetEjectTracking();
    for (const bot of this.state.activeBots.values()) {
      bot.stop();
    }
    this.state.activeBots.clear();
    this.state.isMatchRunning = false;
    this.state.hasFinished = false;
    this.gameoverCtrl.clearTimers();
    this.dmc.stop();

    this.state.lobbyState.status = "lobby";
    this.state.lobbyState.countdownSec = 3;
    if (this.state.isRoomHost()) {
      this.seatCtrl.broadcastLobbyState();
    }
    this.seatCtrl.renderSeatLobby();
    this.dom.elModalGameover.style.display = "none";
    this.dom.elModalSeatLobby.style.display = "flex";
  }

  private returnToModePortal() {
    this.notifyEarlyExitIfInMatch();
    this.clearMatchSnapshotFromSession();
    this.clearAllDisconnectGraceTimers();
    this.syncRoomUrl(null);
    this.boardCtrl.flushPendingMoveAnimation();
    this.combat.resetEjectTracking();
    this.seatCtrl.clearCountdownTimer();
    this.gameoverCtrl.clearTimers();
    for (const bot of this.state.activeBots.values()) {
      bot.stop();
    }
    this.state.activeBots.clear();
    this.state.participants.clear();
    this.state.isMatchRunning = false;
    this.state.hasFinished = false;
    this.state.roomId = null;

    this.p2p.cleanupPeer();
    this.signaling.disconnect();

    this.dom.elModalSeatLobby.style.display = "none";
    this.dom.elModalGameover.style.display = "none";
    this.dom.elModalLobby.style.display = "flex";
    this.portalCtrl.startLobbyPolling();
  }
}

window.addEventListener("DOMContentLoaded", () => {
  const root = document.getElementById("app") || document.body;
  new Game2048PKApp(root);
});
